import React, {useEffect} from 'react';
import {Box, Alert, AlertIcon, Text} from '@chakra-ui/react';
import MapView, {Location} from '../MapView';
import useGetUserLocation from '../../hooks/useUserLocation';
import {setMapPlacesData, setMapCenter} from '../../utils/eventKeywordMappings';
import VenueDistanceEnhancer from '../VenueDistanceEnhancer';

interface EventMapViewProps {
  placesData?: string; // JSON string containing places data
  height?: string | number;
  initialZoom?: number;
  fitBounds?: boolean;
  isMobile?: boolean; // Optional mobile optimization flag
}

interface PlaceData {
  id?: string | number;
  name: string;
  thumbnail: string;
  description: string;
  latitude: number;
  longitude: number;
  actionLink?: string;
  featured?: boolean;
}

const EventMapView: React.FC<EventMapViewProps> = ({
  placesData,
  height = '200px',
  initialZoom = 15,
  fitBounds = true,
  isMobile,
}) => {
  const userLocation = useGetUserLocation();

  // Track map center separately from place data
  const [mapCenter, setMapCenter] = React.useState<
    {latitude: number; longitude: number} | undefined
  >(undefined);

  // Detect mobile viewport if not explicitly provided
  const [isMobileViewport, setIsMobileViewport] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const shouldUseMobileOptimizations = isMobile ?? isMobileViewport;

  // Validate individual place data
  const validatePlace = (place: any): Location | null => {
    if (!place || typeof place !== 'object') {
      return null;
    }

    const {name, thumbnail, description, latitude, longitude, id, actionLink, featured} = place;

    // Normalize number values (handle string representations)
    const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
    const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;

    // Required fields validation with more lenient checks
    // Name and coordinates are truly required
    if (
      typeof name !== 'string' ||
      !name.trim() ||
      typeof lat !== 'number' ||
      isNaN(lat) ||
      typeof lng !== 'number' ||
      isNaN(lng)
    ) {
      console.warn('Invalid place data (missing required fields):', place);
      return null;
    }

    // Validate latitude/longitude ranges
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      console.warn('Invalid coordinates:', {latitude: lat, longitude: lng});
      return null;
    }

    // Use default values for optional fields if missing
    const defaultThumbnail = 'https://via.placeholder.com/150?text=Location';
    const defaultDescription = `Location at coordinates ${lat.toFixed(4)}, ${lng.toFixed(4)}`;

    // Process description to style artist names and times
    let formattedDescription = description;
    if (typeof description === 'string' && description.trim()) {
      // Check if the description contains artist-time pairs (like "Artist - Time")
      const hasPerformers = description.includes(' - ');
      if (hasPerformers) {
        // Split by commas or line breaks to get individual performances
        const performances = description.split(/,|\n/).filter((item) => item.trim());

        // Format each performance with styled artist name
        const formattedPerformances = performances.map((performance) => {
          // Split by " - " to separate artist from time
          const parts = performance.trim().split(' - ');

          if (parts.length >= 2) {
            const artist = parts[0].trim();
            const time = parts.slice(1).join(' - ').trim(); // Join in case there are multiple " - " in the time part

            // Return HTML with styled artist name (blue) and time (black)
            return `<span style="color: #0066cc; font-weight: bold">${artist}</span> &ndash; ${time}`;
          }

          // If can't split, return as is
          return performance.trim();
        });

        // Join performances with line breaks for better readability
        formattedDescription = formattedPerformances.join('<br/>');
      }
    } else {
      formattedDescription = defaultDescription;
    }

    return {
      id: id || `${lat}-${lng}-${name.trim().toLowerCase().replace(/\s+/g, '-')}`,
      name: name.trim(),
      description: formattedDescription,
      latitude: lat,
      longitude: lng,
      actionLink: actionLink && typeof actionLink === 'string' ? actionLink.trim() : undefined,
      featured: featured ? Boolean(featured) : false,
    };
  };

  // Helper function to get place description
  const getPlaceDescription = (place: Location): string => {
    if (!place.description) {
      return '';
    }
    return place.description;
  };
  // Parse and validate places data
  const parsePlacesData = (): Location[] | null => {
    if (!placesData) {
      return null;
    }

    try {
      // First, find and extract content in double square brackets if present
      let jsonData = placesData;

      // Match for specific mapView pattern
      const mapViewRegex = /\[\[mapView\]\](.*?)(?=\[\[|$)/s;
      const mapViewMatch = mapViewRegex.exec(placesData);

      if (mapViewMatch && mapViewMatch[1]) {
        jsonData = mapViewMatch[1].trim();
      } else {
        // Try a more generic pattern for other widgets
        const genericRegex = /\[\[([\w\-_]+)\]\](.*?)(?=\[\[|$)/s;
        const genericMatch = genericRegex.exec(placesData);

        if (genericMatch && genericMatch[2]) {
          jsonData = genericMatch[2].trim();
        }
      }

      // Try to clean up potential JSON issues before parsing
      let cleanedJson = jsonData;

      // Replace common issues in malformed JSON
      if (typeof cleanedJson === 'string') {
        // Remove any HTML entities
        cleanedJson = cleanedJson
          .replace(/&quot;/g, '"')
          .replace(/&nbsp;/g, ' ')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&');

        // Replace line breaks and extra spaces that might interfere with parsing
        cleanedJson = cleanedJson.replace(/[\r\n\t]+/g, ' ').trim();
      }

      // Attempt to parse the cleaned JSON
      const parsed = JSON.parse(cleanedJson);      // Check if there's a mapCenter property we should extract
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.mapCenter) {
        const center = parsed.mapCenter;
        if (
          (center.latitude !== undefined || center.lat !== undefined) &&
          (center.longitude !== undefined || center.lng !== undefined)
        ) {
          const centerCoordinates = {
            latitude: center.latitude || center.lat,
            longitude: center.longitude || center.lng,
          };
          
          // Set local state for MapView
          setMapCenter(centerCoordinates);
          
          // Also share with global state for other components
          setMapCenter(centerCoordinates);
        }
      }

      // Handle single place object
      if (!Array.isArray(parsed)) {
        // Check first for an object with a places property (our preferred format)
        if (parsed && typeof parsed === 'object' && parsed.places && Array.isArray(parsed.places)) {
          return parsed.places.map(validatePlace).filter(Boolean) as Location[];
        }

        // If it's a single object with name and latitude, it's a valid place
        if (parsed && typeof parsed === 'object' && parsed.name && parsed.latitude) {
          return [validatePlace(parsed)].filter(Boolean) as Location[];
        }

        // Check for special cases of nested data structures
        // Case 1: Object containing an array at a top-level property
        const possibleArrays = Object.values(parsed).filter((value) => Array.isArray(value));
        if (possibleArrays.length > 0) {
          // Find the first array that contains place-like objects
          for (const arr of possibleArrays as any) {
            if (
              arr.length > 0 &&
              arr[0] &&
              typeof arr[0] === 'object' &&
              'name' in arr[0] &&
              'latitude' in arr[0]
            ) {
              return (arr as any[]).map(validatePlace).filter(Boolean) as Location[];
            }
          }
        }

        // Case 2: Object with numeric keys indicating it's actually an array
        if (Object.keys(parsed).some((key) => !isNaN(Number(key)))) {
          const arrayData = Object.values(parsed).filter(
            (item) =>
              typeof item === 'object' && item !== null && 'name' in item && 'latitude' in item
          );
          if (arrayData.length > 0) {
            return arrayData.map(validatePlace).filter(Boolean) as Location[];
          }
        }

        // Case 3: Deeply nested array - recursively search for array of place objects
        const findPlacesArray = (obj: any): any[] | null => {
          if (!obj || typeof obj !== 'object') return null;

          for (const key in obj) {
            const value = obj[key];
            if (
              Array.isArray(value) &&
              value.length > 0 &&
              typeof value[0] === 'object' &&
              'name' in value[0] &&
              'latitude' in value[0]
            ) {
              return value;
            } else if (typeof value === 'object') {
              const nestedArray = findPlacesArray(value);
              if (nestedArray) return nestedArray;
            }
          }
          return null;
        };

        const deepNestedArray = findPlacesArray(parsed);
        if (deepNestedArray) {
          return deepNestedArray.map(validatePlace).filter(Boolean) as Location[];
        }

        return null;
      }

      // Handle array of places
      const validPlaces = parsed.map(validatePlace).filter(Boolean) as Location[];
      return validPlaces.map((place) => {
        return {
          ...place,
          description: getPlaceDescription(place),
        };
      });
    } catch (error) {
      console.error('Error parsing places data:', error);

      // Fallback: Try to extract any valid JSON by finding the first { or [ character and matching closing bracket
      if (typeof placesData === 'string') {
        try {
          const startBracketPos = placesData.indexOf('[');
          const startObjectPos = placesData.indexOf('{');

          let startPos = -1;
          let endPos = -1;
          let isArray = false;

          // Find the first valid JSON structure start
          if (startBracketPos >= 0 && (startObjectPos < 0 || startBracketPos < startObjectPos)) {
            startPos = startBracketPos;
            isArray = true;
          } else if (startObjectPos >= 0) {
            startPos = startObjectPos;
            isArray = false;
          }
          if (startPos >= 0) {
            // Now find the matching closing bracket
            const stack: string[] = [];
            const openChars: Record<string, string> = {'[': ']', '{': '}'};
            const closeChars: Record<string, string> = {']': '[', '}': '{'};
            const startChar = isArray ? '[' : '{';
            stack.push(startChar);

            for (let i = startPos + 1; i < placesData.length; i++) {
              const char = placesData[i];
              if (char in openChars) {
                stack.push(char);
              } else if (char in closeChars) {
                if (stack.length > 0 && stack[stack.length - 1] === closeChars[char]) {
                  stack.pop();
                  if (stack.length === 0) {
                    endPos = i;
                    break;
                  }
                }
              }
            }

            if (endPos > startPos) {
              const extractedJson = placesData.substring(startPos, endPos + 1);
              try {
                const extracted = JSON.parse(extractedJson);
                return Array.isArray(extracted)
                  ? (extracted.map(validatePlace).filter(Boolean) as Location[])
                  : ([validatePlace(extracted)].filter(Boolean) as Location[]);
              } catch (e) {
                console.error('Error parsing extracted JSON:', e);
              }
            }
          }
        } catch (e) {
          console.error('Error in JSON extraction fallback:', e);
        }
      }

      return null;
    }
  };
  const places = parsePlacesData();
  // Share places data with YouAreHereCard through the shared global state
  useEffect(() => {
    // Transform Location objects to match the Place interface expected by YouAreHereCard
    if (places && places.length > 0) {
      const placesForYouAreHere = places.map((place) => ({
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
      }));
      setMapPlacesData(placesForYouAreHere);
    } else {
      // Clear the shared state if no places are available
      setMapPlacesData([]);
    }
    // We could share the map center with YouAreHereCard component if needed
    // but we'll use the center directly in MapView instead
  }, [places]);

  if (places === null) {
    return null;
  }

  if (places.length === 0) {
    return (
      <Alert status="warning" borderRadius="md" mx={{base: 2, md: 0}}>
        <AlertIcon />
        <Text fontSize={{base: 'xs', md: 'sm'}} lineHeight="short">
          No spots found in your area yet. Each location needs a name, image, description, and
          coordinates.
        </Text>
      </Alert>
    );
  }
  // Render the map with validated places and mobile optimizations
  
  // Debug log to help identify if mapCenter is properly set
  console.log('Rendering MapView with center:', mapCenter);
  
  return (
    <>
      <Box
        borderRadius={{base: 'md', md: 'lg'}}
        overflow="hidden"
        boxShadow={{base: 'sm', md: 'md'}}
        mx={{base: 2, md: 0}}
        position="relative"
        data-map-venues={JSON.stringify(places)}
        data-has-distances="true"
        data-venue-count={places ? places.length.toString() : '0'}>
        {/* Add venue data as data attribute for distance enhancer */}        <MapView
          articles={places}
          userLocation={
            userLocation?.data
              ? {
                  latitude: userLocation.data.latitude,
                  longitude: userLocation.data.longitude,
                }
              : undefined
          }
          height={height}
          initialZoom={shouldUseMobileOptimizations ? 12 : initialZoom} // Slightly zoomed out for mobile
          fitBounds={mapCenter ? false : fitBounds} // Disable fitBounds if we have a specific mapCenter
          isMobile={shouldUseMobileOptimizations}
          center={mapCenter} // This passes the map center to the MapView component
        />
      </Box>
      {/* Add VenueDistanceEnhancer to show distance information in venue listings */}
      {userLocation?.data && places && places.length > 0 && <VenueDistanceEnhancer />}
    </>
  );
};

export default EventMapView;
